import {updateAgentsStyles} from "../update/update-agent.styles";
import {useDispatch, useSelector} from "react-redux";
import {useHistory, useRouteMatch} from "react-router-dom";
import {AppState} from "../../../store";
import React, {useEffect} from "react";
import {getAgent} from "../store/get-agent.slice";
import {AgentTypes} from "../../../types/service-types/AgentTypes";
import {resetAgentForm, setTheAgentForm} from "../store/agent-form.slice";
import LoadingForm from "../../shared-components/loading-form/LoadingForm";
import {Form, Formik} from "formik";
import {updateAgent} from "../store/update-agent.slice";
import InvoiceInfo from "../components/InvoiceInfo";
import ReservationInfo from "../components/ReservationInfo";
import Credits from "../components/Credits";
import ProvisionInfo from "../components/ProvisionInfo";
import DiscountInfo from "../components/DiscountInfo";
import DebtInfo from "../components/DebtInfo";
import Grid from "@material-ui/core/Grid";
import LoadingButton from "../../shared-components/loading-button/LoadingButton";
import {Button} from "@material-ui/core";
import PageHeader from "../../shared-components/page-header/PageHeader";
import {useTranslation} from "react-i18next";
import AgentEditInformation from "../components/AgentEditInformation";
import {setAgentIdentificationForm} from "../store/agent-identification-form.slice";
import {setAgentScopesForm} from "../store/agent-scopes-form.slice";
import {resetAgentLogoImage, updateAgentImage} from "../store/agent-logo-image.slice";
import AppUtils from "../../../utils/AppUtils";
import useAuthority from "app/hooks/useAuthority";
import {ROLES} from "app/constants/roles";


const AgentsEditPage = () => {
    const classes = updateAgentsStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const history = useHistory();
    const {params}: any = useRouteMatch();
    const agentLoading = useSelector((state: AppState) => state.agents.agent.loading);
    const updateAgentLoading = useSelector((state: AppState) => state.agents.updateAgent.loading);
    const agentForm = useSelector((state: AppState) => state.agents.agentForm);
    const agentLogoImage = useSelector((state: AppState) => state.agents.agentLogoImage);
    const {hasUserAnyAuthority} = useAuthority();
    
    const isAdmin = hasUserAnyAuthority([ROLES.ROLE_ADMIN]);
    const isAgent = hasUserAnyAuthority([ROLES.ROLE_AGENT]) && !isAdmin;

    useEffect(() => {
        dispatch(getAgent({
            id: params.id,
            onSuccess: (responseData: AgentTypes) => fillFormWithSelectedAgent(responseData),
        }));
        dispatch(resetAgentLogoImage());
    }, []);

    const fillFormWithSelectedAgent = (agentData: AgentTypes) => {
    if (agentData) {
        const formatBalanceRequests =
            agentData.balances && agentData.balances.length > 0
                ? agentData.balances.map((item) => ({
                    id: item.id,
                    availableCredit: item.availableCredit ?? 0,
                    creditLimit: item.creditLimit ?? "",
                    neto: item.neto,
                    minimumTicketPrice: item.minimumTicketPrice,
                    currencyId: item.currencyResponse?.id ?? ""
                }))
                : [
                    {
                        id: `temp-credit-${Date.now()}`,
                        availableCredit: 0,
                        creditLimit: "",
                        neto: null,
                        minimumTicketPrice: null,
                        currencyId: ""
                    }
                ];

        const provisionFormsFormatted = agentData.provisionForms.map((p) => {
            return {
                ...p,
                currencyId: p.currency.id
            };
        });

        const discountsFormatted = agentData.discounts.map((p) => {
            return {
                ...p,
                currencyId: p.currency.id
            };
        });

        dispatch(
            setAgentIdentificationForm({
                email: agentData.email,
                status: agentData.status
            })
        );

        dispatch(
            setAgentScopesForm({
                scopes: agentData.scopes
            })
        );

        dispatch(
            setTheAgentForm({
                fullName: agentData.fullName,
                agencyName: agentData.agencyName,
                contactEmail: agentData.contactEmail,
                location: agentData.location,
                phoneNumber: agentData.phoneNumber,
                companyName: agentData.companyName,
                address: agentData.address,
                subAgentName: agentData.subAgentName || "",
                subAgentSurname: agentData.subAgentSurname || "",
                invoiceText: agentData.invoiceText,
                invoiceBottomText: agentData.invoiceBottomText,
                maxAdultAllowed: agentData.maxAdultAllowed,
                maxChildAllowed: agentData.maxChildAllowed,
                maxInfantAllowed: agentData.maxInfantAllowed,
                allowInfantOnly: agentData.allowInfantOnly,
                provisionFormType: agentData.provisionFormType,
                provisionForms: provisionFormsFormatted,
                provisionPercentage: agentData.provisionPercentage,
                balanceRequests: formatBalanceRequests,
                discountType: agentData.discountType,
                discounts: discountsFormatted,
                allowedDiscount: agentData.allowedDiscount,
                actualDebt: agentData.actualDebt,
                debtBeforeEnding: agentData.debtBeforeEnding,
                isOperatorLogo: agentData.isOperatorLogo
            })
        );
    }
};

    if (agentLoading) {
        return <LoadingForm/>;
    }

    const pageTitle = isAgent 
        ? `${t("app.main.agency.agentsList.subAgentEdit.title", "Sub-Agent information")} ${" "} ${agentForm.fullName}`
        : `${t("app.main.agency.agentsList.agentEdit.title", "Agent information")} ${" "} ${agentForm.fullName}`;

    return (
        <div>
            <PageHeader
                title={pageTitle}
                desc={t("app.main.agency.agentsList.agentEdit.desc", "To change the information, change the form below and click Save Changes")}
                backButton={() => {
                    dispatch(resetAgentForm());
                    history.push("/agents-agencies/list");
                }}
            />
            <div className={classes.formWrapper}>
                <Formik
                    initialValues={agentForm}
                    onSubmit={() => {
    const firstProvisionForm = agentForm.provisionForms?.[0];

    const formWithoutBalanceId = {
        ...agentForm,

        // IMPORTANT: keep backend root provisionPercentage synced
        provisionPercentage:
            agentForm.provisionFormType !== "FIXED"
                ? Number(firstProvisionForm?.provisionPercentage ?? 0)
                : 0,

        provisionForms: agentForm.provisionForms.map((item) => ({
            ...item,
            provisionPercentage:
                agentForm.provisionFormType !== "FIXED"
                    ? Number(item.provisionPercentage ?? 0)
                    : 0,
            minimumProvision:
                item.minimumProvision !== null && item.minimumProvision !== undefined 
                    ? Number(item.minimumProvision)
                    : null,
            maximumProvision:
                item.maximumProvision !== null && item.maximumProvision !== undefined 
                    ? Number(item.maximumProvision)
                    : null,
            fixedProvision:
                item.fixedProvision !== null && item.fixedProvision !== undefined 
                    ? Number(item.fixedProvision)
                    : null,
            neto:
                item.neto !== null && item.neto !== undefined 
                    ? Number(item.neto)
                    : null,
            currencyId: item.currencyId,
        })),

        balanceRequests: agentForm.balanceRequests.map(({ id, ...items }) => items),
    };

    if (agentLogoImage.logoImage) {
        AppUtils.dataUrlToFile(agentLogoImage.logoImage, "logoImage").then((res: File) => {
            dispatch(updateAgentImage(params.id, res));
        });
    }

    dispatch(
        updateAgent({
            id: params.id,
            body: formWithoutBalanceId,
            onSuccess: () => {
                history.push("/agents-agencies/list");
                dispatch(resetAgentForm());
            },
        })
    );
}}
                >
                    <Form>
                         <AgentEditInformation/>
                          <InvoiceInfo />
                          <ReservationInfo />
                          <Credits />
                          <ProvisionInfo />
                          <DiscountInfo />
                          <DebtInfo />          
                        <Grid container justify='flex-end' alignItems='center'
                              style={{marginTop: '20px'}}>
                            <Grid item>
                                <LoadingButton
                                    type='submit'
                                    btnTitle={t("app.main.flights.flightList.editPage.saveChanges", "Save Changes")}
                                    size='medium'
                                    isLoading={updateAgentLoading}
                                />

                                <Button
                                    onClick={(e) => {
                                        dispatch(resetAgentForm());
                                        history.push('/agents-agencies/list')
                                    }}>
                                    {t("app.main.schedules.splitPassenger.closeButton", "Close")}
                                </Button>
                            </Grid>
                        </Grid>
                    </Form>
                </Formik>
            </div>
        </div>
    );
};

export default AgentsEditPage;
